import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { HttpError } from 'src/app/core/http-error';
import { Rights } from 'src/app/models/enums/rights';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { FormMode } from '../../controls/common/form-mode';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { Application } from '../../models/application';
import { IApplication } from '../../models/interfaces/application.interface';
import { ApplicationsService } from '../../services/applications.service';
import { GroupsTreeComponent } from '../groups/groups-tree/groups-tree.component';
import { ApplicationDetailContext } from './application-detail-context';
import { ApplicationsDetailComponent } from './applications-detail/applications-detail.component';
import { ApplicationsListComponent } from './applications-list/applications-list.component';

enum DisplayMode {
    List = 1,
    Tree = 2,
    Form = 4,
}
@Component({
    selector: 'app-applications',
    templateUrl: './applications.component.html',
    styleUrl: './applications.component.scss',
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ApplicationsListComponent, DetailHeaderComponent, ApplicationsDetailComponent],
})
export class ApplicationsComponent implements OnInit {
    /**
     * Reference au composant ApplicationsList.
     */
    @ViewChild(ApplicationsListComponent) applicationsList: ApplicationsListComponent;

    /**
     * Reference au composant GroupsTree.
     */
    @ViewChild(GroupsTreeComponent) groupsTree: GroupsTreeComponent;

    /**
     * Liste des icônes utilisés dans le template
     */
    public faUser = faUser;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    public context: ApplicationDetailContext = null;

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'édition doit apparaitre.
     */
    public displayEditbuttom = false;

    /**
     * Constructeur
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param applicationsService : Service métier utilisé pour gérer les applications.
     * @param paramsValuesService : Service métier utilisé pour gérer les paramètres.
     * @param memoryService : Service utilisé pour gérer le contexte courant.
     */
    constructor(
        protected notificationService: NotificationService,
        private applicationsService: ApplicationsService,
        private paramsValuesService: ParamsValuesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.None);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne une application.
     * @param application Application selectionnée.
     */
    public onApplicationSelected(application: Application) {
        this.reloadForm(application, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param application Application sélectionnée.
     */
    public onApplicationDisplayed(application: Application) {
        this.reloadForm(application, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param application Application sélectionnée.
     */
    public onApplicationEdited(application: Application) {
        this.reloadForm(application, FormMode.Edition);
    }

    /**
     * Appelé lorsque les données de l'application ont été mise à jour.
     * Il s'agit des modifications apportées à l'onglet Infos.
     * @param group : Données de l'application à modifier.
     */
    public applicationUpdated(application: Application): void {
        this.spinner.show('main-spinner');
        if (this.context.editedApplicationId) {
            // Cas de la mise à jour.
            this.applicationsService.update(this.context.editedApplicationId, application).subscribe(
                (x) => {
                    this.applicationsList.refresh();
                    this.displaySuccessNotification('Application mise à jour');
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour l'application." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur confirme la réinitialisation d'une application.
     * @param application Application sélectionné.
     */
    public onApplicationReseted(application: Application) {
        this.spinner.show('main-spinner');
        // Réinitialisation effective de l'application.
        this.applicationsService.reset(application.id).subscribe(
            (x) => {
                this.applicationsList.refresh();
                this.displaySuccessNotification('Application réinitialisée');
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                console.error(error);
                this.notificationService.displayError('Erreur', "Impossible de réinitialiser l'application." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton d'export.
     */
    public onApplicationsExported() {
        this.applicationsList.export('Applications.xlsx');
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton d'aide.
     */
    public onApplicationsHelped() {
        this.applicationsList.displayHelpDialog('applications');
    }

    /**
     * Retourne une valeur indiquant si le formulaire est affiché (visible)
     * @returns True si le formulaire est visible.
     */
    public isFormDisplayed(): boolean {
        return DisplayMode.Form === (this.displayMode & DisplayMode.Form);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des groupes sous la forme d'une liste est actif.
     * L'affichage des groupes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des groupes sous la forme d'une liste est actif.
     */
    public isListDisplayed(): boolean {
        return DisplayMode.List === (this.displayMode & DisplayMode.List);
    }

    /**
     * Cache le formulaire.
     * La liste ou l'arboresence est alors visible.
     */
    public hideForm(): void {
        this.displayMode &= ~DisplayMode.Form;
    }

    /**
     * Définit le mode d'affichage de la page (Form / List).
     * @param mode Mode d'affichage
     */
    public setDisplayMode(mode: DisplayMode) {
        if (mode == DisplayMode.Form) {
            this.displayMode |= mode;
        } else {
            this.displayMode = mode;
        }
    }

    /**
     * Charge les informations d'une application.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param applicationId : Id de l'application à charger.
     * @returns Les informations de l'application.
     */
    private loadApplication(applicationId: string): Observable<Application> {
        return this.applicationsService.get(applicationId);
    }

    /**
     * Affiche Les données d'une application dans le formulaire.
     * @param group : Instance de l'application à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    private displayForm(application: IApplication, formMode: FormMode): void {
        this.createContext(application, formMode);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Recharge le formulaire pour l'application spécifiée
     * @param group Instance de l'application à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    private reloadForm(application: IApplication, formMode: FormMode): void {
        this.spinner.show('main-spinner');
        this.loadApplication(application.id).subscribe(
            (x) => {
                this.displayForm(x, formMode);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'application." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Affiche une notification en cas de succès d'une action serveur.
     * @param notificationText: Texte de la notification.
     */
    private displaySuccessNotification(notificationText: string): void {
        this.notificationService.displaySuccess(null, notificationText);
    }

    /**
     * Crée un contexte utilisé pour l'affichage du formulaire.
     * @param application : Instance de l'application à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     * @returns Le contexte utilisé pour l'affichage du formulaire.
     */
    private createContext(application: IApplication, formMode: FormMode): void {
        let context = new ApplicationDetailContext();
        context.applicationContext.item = application;
        context.editedApplicationId = application.id;
        context.formMode = formMode;

        this.context = context;
    }
}
